import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Popover, Tooltip } from 'antd';
import {
  BoldOutlined,
  ClearOutlined,
  DisconnectOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { domToStored, storedToHtml } from './richText';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const MOD = isMac ? '⌘' : 'Ctrl';

const closestIn = (node, root, test) => {
  let el = node && node.nodeType === 3 ? node.parentNode : node;
  while (el && el !== root) {
    if (test(el)) return el;
    el = el.parentNode;
  }
  return null;
};

const isLargeEl = (el) =>
  (el.tagName === 'FONT' && Number(el.getAttribute('size')) >= 4) ||
  (el.style && el.style.fontSize && (parseFloat(el.style.fontSize) > 1.1 || /large/.test(el.style.fontSize)));

/**
 * A formatting editor for dynamic form text (field descriptions, form instructions, banner caption).
 * Formatting shows as it will look; the value is the stored text format the public page reads,
 * so it works with Form.Item like a TextArea.
 *
 * size: 'description' (14px), 'details' (16px) or 'caption' (15px, muted) — the sizes used on the page.
 */
const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  size = 'description',
  align = 'left',
  minHeight = 96,
  readOnly = false,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  const areaRef = useRef(null);
  const lastValue = useRef(null);
  const savedRange = useRef(null);
  const [active, setActive] = useState({});
  const [empty, setEmpty] = useState(!value);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkNeedsText, setLinkNeedsText] = useState(false);
  const [editingLink, setEditingLink] = useState(false);

  // Load stored text into the editor whenever it changes from outside (opening another question, say).
  useEffect(() => {
    const next = value || '';
    if (next === lastValue.current) return;
    lastValue.current = next;
    if (areaRef.current) areaRef.current.innerHTML = storedToHtml(next);
    setEmpty(!next);
  }, [value]);

  const emit = useCallback(() => {
    const root = areaRef.current;
    if (!root) return;
    const next = domToStored(root);
    setEmpty(!next && !root.textContent.trim());
    if (next !== lastValue.current) {
      lastValue.current = next;
      onChange?.(next);
    }
  }, [onChange]);

  const refreshActive = useCallback(() => {
    const root = areaRef.current;
    const sel = window.getSelection();
    if (!root || !sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) return;
    const query = (cmd) => {
      try {
        return document.queryCommandState(cmd);
      } catch {
        return false;
      }
    };
    setActive({
      bold: query('bold'),
      italic: query('italic'),
      underline: query('underline'),
      list: query('insertUnorderedList'),
      numbered: query('insertOrderedList'),
      large: !!closestIn(sel.anchorNode, root, isLargeEl),
      link: !!closestIn(sel.anchorNode, root, (el) => el.tagName === 'A'),
    });
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  const exec = (command, arg = null) => {
    areaRef.current?.focus();
    try {
      document.execCommand('styleWithCSS', false, false);
      document.execCommand('defaultParagraphSeparator', false, 'div');
    } catch {
      // older browsers ignore these
    }
    document.execCommand(command, false, arg);
    emit();
    refreshActive();
  };

  const toggleLarge = () => exec('fontSize', active.large ? '3' : '5');

  const clearFormatting = () => {
    exec('removeFormat');
    exec('unlink');
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    const root = areaRef.current;
    if (sel && sel.rangeCount > 0 && root && root.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRange.current = null;
    }
  };

  const restoreSelection = () => {
    const root = areaRef.current;
    root?.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else if (root && sel) {
      // no saved place: put the caret at the end
      const range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const openLink = () => {
    saveSelection();
    const root = areaRef.current;
    const anchor = savedRange.current && closestIn(savedRange.current.startContainer, root, (el) => el.tagName === 'A');
    setEditingLink(!!anchor);
    setLinkUrl(anchor ? anchor.getAttribute('href') || '' : '');
    setLinkNeedsText(!anchor && (!savedRange.current || savedRange.current.collapsed));
    setLinkText('');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!url) return;
    restoreSelection();
    const root = areaRef.current;
    const anchor = savedRange.current && closestIn(savedRange.current.startContainer, root, (el) => el.tagName === 'A');
    if (anchor) {
      anchor.setAttribute('href', url);
      emit();
      return;
    }
    if (linkNeedsText) {
      const text = linkText.trim() || url;
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.textContent = text;
      exec('insertHTML', a.outerHTML);
    } else {
      exec('createLink', url);
    }
  };

  const removeLink = () => {
    setLinkOpen(false);
    restoreSelection();
    const root = areaRef.current;
    const anchor = savedRange.current && closestIn(savedRange.current.startContainer, root, (el) => el.tagName === 'A');
    if (anchor) {
      const range = document.createRange();
      range.selectNodeContents(anchor);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    exec('unlink');
  };

  const onKeyDown = (e) => {
    const mod = isMac ? e.metaKey : e.ctrlKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 'b' || key === 'i' || key === 'u') {
      e.preventDefault();
      exec(key === 'b' ? 'bold' : key === 'i' ? 'italic' : 'underline');
    } else if (key === 'k') {
      e.preventDefault();
      openLink();
    }
  };

  // Pasted text keeps its words, not the formatting of wherever it came from.
  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    emit();
  };

  const tool = (key, label, shortcut, icon, onClick, pressed) => (
    <Tooltip title={shortcut ? `${label} (${shortcut})` : label} mouseEnterDelay={0.4}>
      <button
        type="button"
        className={`dfw-rte__tool${pressed ? ' is-on' : ''}`}
        aria-label={label}
        aria-pressed={pressed === undefined ? undefined : !!pressed}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        key={key}
      >
        {icon}
      </button>
    </Tooltip>
  );

  const linkForm = (
    <div className="dfw-rte__link">
      <label>
        <span>Web address</span>
        <Input
          autoFocus
          value={linkUrl}
          placeholder="https://"
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={applyLink}
        />
      </label>
      {linkNeedsText && (
        <label>
          <span>Text to show</span>
          <Input value={linkText} placeholder="e.g. Read the terms" onChange={(e) => setLinkText(e.target.value)} onPressEnter={applyLink} />
        </label>
      )}
      <div className="dfw-rte__link-actions">
        {editingLink && (
          <Button size="small" type="text" danger icon={<DisconnectOutlined aria-hidden="true" />} onClick={removeLink}>
            Remove link
          </Button>
        )}
        <span className="dfw-grow" />
        <Button size="small" onClick={() => setLinkOpen(false)}>Cancel</Button>
        <Button size="small" type="primary" onClick={applyLink} disabled={!linkUrl.trim()}>
          {editingLink ? 'Update link' : 'Add link'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={`dfw-rte dfw-rte--${size}${readOnly ? ' is-readonly' : ''}`}>
      {!readOnly && <div className="dfw-rte__bar" role="toolbar" aria-label="Text formatting">
        {tool('b', 'Bold', `${MOD}+B`, <BoldOutlined aria-hidden="true" />, () => exec('bold'), active.bold)}
        {tool('i', 'Italic', `${MOD}+I`, <ItalicOutlined aria-hidden="true" />, () => exec('italic'), active.italic)}
        {tool('u', 'Underline', `${MOD}+U`, <UnderlineOutlined aria-hidden="true" />, () => exec('underline'), active.underline)}
        {tool('l', 'Larger text', null, <FontSizeOutlined aria-hidden="true" />, toggleLarge, active.large)}
        <span className="dfw-rte__sep" aria-hidden="true" />
        {tool('ul', 'Bulleted list', null, <UnorderedListOutlined aria-hidden="true" />, () => exec('insertUnorderedList'), active.list)}
        {tool('ol', 'Numbered list', null, <OrderedListOutlined aria-hidden="true" />, () => exec('insertOrderedList'), active.numbered)}
        <Popover
          open={linkOpen}
          onOpenChange={(open) => { if (!open) setLinkOpen(false); }}
          trigger="click"
          placement="bottomLeft"
          content={linkForm}
          rootClassName="do-scope dfw-rte-pop"
          destroyOnHidden
        >
          <span>{tool('a', 'Link', `${MOD}+K`, <LinkOutlined aria-hidden="true" />, openLink, active.link)}</span>
        </Popover>
        <span className="dfw-rte__sep" aria-hidden="true" />
        {tool('x', 'Clear formatting', null, <ClearOutlined aria-hidden="true" />, clearFormatting)}
      </div>}
      <div className={`dfw-rte__wrap${empty ? ' is-empty' : ''}`} data-placeholder={placeholder}>
        <div
          ref={areaRef}
          id={id}
          className="dfw-rte__area"
          contentEditable={!readOnly}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          tabIndex={0}
          spellCheck
          style={{ minHeight, textAlign: align }}
          onInput={emit}
          onBlur={emit}
          onKeyDown={onKeyDown}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          onPaste={onPaste}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
